import FormattedMessage from 'I18n/components/FormattedMessage';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { useMutation } from 'data-fetching-client';
import { useDispatch, useSelector } from 'react-redux';
import { useCMPPublishActionHandlers } from 'ContentEditorUI/components/publishing/hooks/usePublishActionHandlers';
import EditorPublishButton from 'ContentEditorUI/components/shared/EditorPublishButton';
// @ts-expect-error not typed
import { showPublishErrors } from 'ContentEditorUI/redux/actions/publishActions';
import { getCategoryId } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { displayPublishSuccessAlert } from 'ContentEditorUI/utils/displayPublishSuccessAlert';
import { ContentObjectTypes } from 'content-management-data-lib';
import { updateInstanceValues, fetchInstanceData } from 'structured-content-lib/redux/actions/instanceEditActions';
import { selectActiveHubDbRowId } from 'structured-content-lib/redux/selectors/instanceEditSelectors';
import { selectHasArticlePublishPermission, selectIsUngatedForSchedulingWithContentCrmObject } from '../../auth/selectors/auth';
import { GET_SMART_COPY_ARTICLE_WARNING, REVIEW_IMPORT_WARNING } from '../../import/queries';
import { usageTracker } from '../../utils/usageTracker';
import { updateFocusedItem } from '../actions/articleFocusActions';
import { save } from '../actions/editorSaveActions';
import { selectArticleErrorLabels, selectArticleWarningLabels } from '../selectors/articleEditorWarningsAndErrors';
import { selectArticleUrl, selectButtonTextKey, selectIsArticlePublished, selectArticleAIGenerated } from '../selectors/editorSelectors';
import { PublishTypes } from 'ContentEditorUI/constants/PublishTypes';
import useContentCrmObject from 'ContentEditorUI/components/publishing/hooks/useContentCrmObject';
import { fetchArticle } from '../../articles/api/articlesApi';
// @ts-expect-error not typed
import { onFetchContentSchemaFailed } from 'ContentEditorUI/redux/actions/appActions';
import { jsx as _jsx } from "react/jsx-runtime";
const usePublishUnpublishActionHandlers = () => {
  const articleId = useSelector(selectActiveHubDbRowId);
  const isAiGenerated = useSelector(selectArticleAIGenerated);
  const dispatch = useDispatch();
  const categoryId = useSelector(getCategoryId);
  const appState = useSelector(state => state);
  const isUngatedForSchedulingWithContentCrmObject = useSelector(selectIsUngatedForSchedulingWithContentCrmObject);
  const isPublished = useSelector(selectIsArticlePublished);
  const scpPublishType = isUngatedForSchedulingWithContentCrmObject ? undefined : isPublished ? PublishTypes.update : PublishTypes.publish;
  const url = useSelector(selectArticleUrl);
  const {
    canScheduleWithContentCrmObject
  } = useContentCrmObject();
  const [reviewImportWarning] = useMutation(REVIEW_IMPORT_WARNING, {
    variables: {
      articleId
    },
    refetchQueries: [GET_SMART_COPY_ARTICLE_WARNING]
  });
  const refetchInstance = () => {
    dispatch(
    // @ts-expect-error tableName param should be optional
    fetchInstanceData(undefined, fetchArticle.bind(null, articleId))).catch(result => dispatch(onFetchContentSchemaFailed({
      result
    })));
  };
  const onPublishSuccess = (data, publishType) => {
    displayPublishSuccessAlert({
      contentType: 'knowledgeArticle',
      publishType: scpPublishType || publishType || PublishTypes.publish,
      publishedContentUrl: url,
      canScheduleWithContentCrmObject
    });
    if (canScheduleWithContentCrmObject) {
      refetchInstance();
    } else {
      dispatch(updateInstanceValues(data));
    }
  };
  const onUnpublishSuccess = data => {
    FloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unpublishModal.success.message"
      }),
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unpublishModal.success.text"
      }),
      type: 'success'
    });
    if (canScheduleWithContentCrmObject) {
      refetchInstance();
    } else {
      dispatch(updateInstanceValues(data));
    }
  };
  const onPublishError = errorResponse => {
    FloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "publishModal.publishFailed"
      }),
      type: 'danger'
    });
    showPublishErrors(errorResponse.responseJSON || {}, categoryId, appState);
  };
  const onUnpublishError = errorResponse => {
    FloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unpublishModal.error.message"
      }),
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unpublishModal.error.text"
      }),
      type: 'danger'
    });
    showPublishErrors(errorResponse.responseJSON || {}, categoryId, appState);
  };
  const trackPublishEvent = () => {
    const isUpdate = scpPublishType === 'update';
    usageTracker.track('editorInteraction', {
      event: 'publish article',
      subtype: 'button click',
      isUpdate,
      // isAiInteraction is true when publishing an article that is determined to have been AI generated.
      isAiInteraction: !isUpdate && Boolean(isAiGenerated),
      isOriginallyAiGenerated: Boolean(isAiGenerated)
    });
  };
  const {
    publish,
    unpublish,
    schedulePublishUnpublish
  } = useCMPPublishActionHandlers({
    saveAction: save,
    onPublishSuccess,
    onPublishError,
    onUnpublishSuccess,
    onUnpublishError,
    trackPublishEvent,
    contentObjectType: ContentObjectTypes.KNOWLEDGE_ARTICLE,
    objectId: articleId,
    shouldConvertCMPObjectToInstanceObject: true
  });
  return {
    publish: publishType => {
      reviewImportWarning().catch(reportError);
      publish(publishType);
    },
    unpublish,
    schedulePublishUnpublish
  };
};
const ArticlePublishButtonContainer = () => {
  const errors = useSelector(selectArticleErrorLabels);
  const warnings = useSelector(selectArticleWarningLabels);
  const publishEnabled = useSelector(selectHasArticlePublishPermission);
  const isPublished = useSelector(selectIsArticlePublished);
  const buttonTextKey = useSelector(selectButtonTextKey);
  const isUngatedForSchedulingWithContentCrmObject = useSelector(selectIsUngatedForSchedulingWithContentCrmObject);
  const {
    publish,
    unpublish,
    schedulePublishUnpublish
  } = usePublishUnpublishActionHandlers();
  return /*#__PURE__*/_jsx(EditorPublishButton, {
    errors: errors,
    warnings: warnings,
    canSchedule: isUngatedForSchedulingWithContentCrmObject,
    publishEnabled: publishEnabled,
    unpublishEnabled: isPublished,
    buttonTextKey: buttonTextKey,
    updateFocusedItemAction: updateFocusedItem,
    publish: publish,
    unpublish: unpublish,
    schedulePublishUnpublish: schedulePublishUnpublish,
    schedulingOptionsModalProps: {
      canScheduleUnpublish: isUngatedForSchedulingWithContentCrmObject
    },
    isNotAuthorizedToPublish: !publishEnabled
  });
};
export default ArticlePublishButtonContainer;